import * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import { DateShortRelative } from '@gitbook/react-uikit';

import {
    ChangeRequestReviewButton,
    ChangeRequestStatusBadge,
    ChangeRequestSubject,
    ChangeRequestUnarchiveButton,
    UserProfilePrettyName,
} from '../../../Components';
import { useLoggedInUser } from '../../../state';
import { ChangeRequestDescription } from './ChangeRequestDescription';
import { ChangeRequestDetailsResponsive } from './ChangeRequestDetails';
import { ChangeRequestMergeBlock } from './ChangeRequestMergeBlock';

export type ChangeRequestIntroProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
};

/**
 * Intro presenting the change requests: its subject, description, status, etc.
 * With key actions to merge and review.
 */
export function ChangeRequestIntro(props: ChangeRequestIntroProps) {
    const { space, changeRequest, analyticsContext } = props;
    const currentUser = useLoggedInUser();

    return (
        <div className="flex flex-col gap-2 py-4">
            <div className="flex flex-row items-center gap-2 pr-6 pl-4">
                <ChangeRequestSubject
                    space={space}
                    changeRequest={changeRequest}
                    size="large"
                    editable
                />
                <span className="shrink-0 font-light text-muted text-ui-heading-large">
                    #{changeRequest.number}
                </span>
            </div>
            <div className="flex flex-col gap-4 px-6">
                <div className="flex flex-row items-center gap-2">
                    <ChangeRequestStatusBadge status={changeRequest.status} size="small" />
                    <div className="text-secondary">
                        Created{' '}
                        <DateShortRelative value={changeRequest.createdAt} onlyDate={false} /> by{' '}
                        <UserProfilePrettyName user={changeRequest.createdBy} />
                    </div>
                </div>

                <ChangeRequestDetailsResponsive changeRequest={changeRequest} space={space} />

                <ChangeRequestDescription
                    space={space}
                    changeRequest={changeRequest}
                    analyticsContext={analyticsContext}
                />
                <div className="flex items-center justify-between">
                    {changeRequest.status === api.ChangeRequestStatus.Open ||
                    changeRequest.status === api.ChangeRequestStatus.Draft ? (
                        <ChangeRequestMergeBlock {...props} />
                    ) : null}
                    {changeRequest.status === api.ChangeRequestStatus.Archived ? (
                        <ChangeRequestUnarchiveButton space={space} changeRequest={changeRequest} />
                    ) : null}
                </div>
                {(changeRequest.status === api.ChangeRequestStatus.Open ||
                    changeRequest.status === api.ChangeRequestStatus.Draft) &&
                space.permissions.review &&
                currentUser.uid !== changeRequest.createdBy.id ? (
                    <ChangeRequestReviewButton
                        space={space.id}
                        changeRequest={changeRequest.id}
                        className="shrink-0"
                    />
                ) : null}
            </div>
        </div>
    );
}
